import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    getItemDepByType,
    getAllItemDepPercentage,
    saveItemDepreciationPercent,
} from '../../api/PurchaseAPI/itemDepreciationConfigAPI';

// ── Async Thunks ───────────────────────────────────────────────────────────────

/** Grid of all configured item depreciation percentages (Issue / Receive) */
export const fetchItemDepGrid = createAsyncThunk(
    'itemDepreciationConfig/fetchGrid',
    async (_, { rejectWithValue }) => {
        try {
            const res = await getAllItemDepPercentage();
            return res.data?.Data || [];
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the item depreciation grid');
        }
    }
);

/** Currently configured percentage for a Type — used to prefill the form on Edit */
export const fetchItemDepByType = createAsyncThunk(
    'itemDepreciationConfig/fetchByType',
    async (type, { rejectWithValue }) => {
        try {
            const res = await getItemDepByType(type);
            return res.data?.Data ?? 0;
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to load the current percentage');
        }
    }
);

/** Add / Edit / Delete the depreciation percentage for a Type */
export const submitItemDepreciationPercent = createAsyncThunk(
    'itemDepreciationConfig/save',
    async ({ Percentage, Type, Action, CreatedBy, RoleId }, { rejectWithValue }) => {
        try {
            const res = await saveItemDepreciationPercent({ Percentage, Type, Action, CreatedBy, RoleId });
            const status = res.data?.Data;
            if (status !== 'Submited') {
                return rejectWithValue(status || 'Failed to save the item depreciation percentage');
            }
            return { Type, Percentage, Action };
        } catch (err) {
            return rejectWithValue(err?.response?.data?.Message || err.message || 'Failed to save the item depreciation percentage');
        }
    }
);

// ── Slice ──────────────────────────────────────────────────────────────────────

const initialState = {
    gridList: [],
    currentPercent: null,
    saveResult: null,

    loading: {
        grid: false,
        byType: false,
        save: false,
    },
    errors: {
        grid: null,
        byType: null,
        save: null,
    },
};

const itemDepreciationConfigSlice = createSlice({
    name: 'itemDepreciationConfig',
    initialState,
    reducers: {
        clearItemDepByType: (s) => { s.currentPercent = null; s.errors.byType = null; },
        clearItemDepSaveResult: (s) => { s.saveResult = null; s.errors.save = null; },
        resetItemDepreciationConfig: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchItemDepGrid.pending, (s) => { s.loading.grid = true; s.errors.grid = null; })
            .addCase(fetchItemDepGrid.fulfilled, (s, a) => { s.loading.grid = false; s.gridList = a.payload; })
            .addCase(fetchItemDepGrid.rejected, (s, a) => { s.loading.grid = false; s.errors.grid = a.payload; });

        builder
            .addCase(fetchItemDepByType.pending, (s) => { s.loading.byType = true; s.errors.byType = null; s.currentPercent = null; })
            .addCase(fetchItemDepByType.fulfilled, (s, a) => { s.loading.byType = false; s.currentPercent = a.payload; })
            .addCase(fetchItemDepByType.rejected, (s, a) => { s.loading.byType = false; s.errors.byType = a.payload; });

        builder
            .addCase(submitItemDepreciationPercent.pending, (s) => { s.loading.save = true; s.errors.save = null; s.saveResult = null; })
            .addCase(submitItemDepreciationPercent.fulfilled, (s, a) => { s.loading.save = false; s.saveResult = a.payload; })
            .addCase(submitItemDepreciationPercent.rejected, (s, a) => { s.loading.save = false; s.errors.save = a.payload; });
    },
});

export const {
    clearItemDepByType,
    clearItemDepSaveResult,
    resetItemDepreciationConfig,
} = itemDepreciationConfigSlice.actions;

// ── Selectors ──────────────────────────────────────────────────────────────────
export const selectItemDepGridList     = (s) => s.itemDepreciationConfig.gridList;
export const selectItemDepCurrentPercent = (s) => s.itemDepreciationConfig.currentPercent;
export const selectItemDepSaveResult   = (s) => s.itemDepreciationConfig.saveResult;

export const selectItemDepGridLoading   = (s) => s.itemDepreciationConfig.loading.grid;
export const selectItemDepGridError     = (s) => s.itemDepreciationConfig.errors.grid;
export const selectItemDepByTypeLoading = (s) => s.itemDepreciationConfig.loading.byType;
export const selectItemDepByTypeError   = (s) => s.itemDepreciationConfig.errors.byType;
export const selectItemDepSaveLoading   = (s) => s.itemDepreciationConfig.loading.save;
export const selectItemDepSaveError     = (s) => s.itemDepreciationConfig.errors.save;

export default itemDepreciationConfigSlice.reducer;
